import React, { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Drawer, Button, Input, Select, Switch, Radio, TimePicker,
  DatePicker, Empty, Spin, Tooltip, Badge, Checkbox, Tag, message,
  Tabs, Avatar, Modal,
} from "antd";
import {
  CarOutlined, PlusOutlined, QuestionCircleOutlined,
  EditOutlined, DeleteOutlined, UserOutlined, CalendarOutlined,
  CopyOutlined,
  CloseCircleOutlined, EnvironmentOutlined,
} from "@ant-design/icons";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { GMAPS_KEY, GMAPS_LIBS } from "./constants";
import { parseAddresses, getAgentColor, agentInitials, _fetchSRMUsers, fetchMiscOptions, fetchRegionOptions, fetchFuelTypeOptions, engineLayoutLabel, useQv4Theme } from "./helpers";
import { PIPELINE_COLUMNS, URGENCY_CFG } from "./pipeline";
import { WorkflowTaskDrawer } from "./WorkflowTaskDrawer";
import { SendToSuppliersModal } from "./SendToSuppliersModal";
import { PartsEditor, EMPTY_PART } from "./PartsEditor";
import { QueryImagePicker } from "./QueryImagePicker";

const getStatusDisplay = (status) => {
  const col = PIPELINE_COLUMNS.find(c => !c.isChat && c.statuses?.includes(status));
  return col ? { label: col.label, color: col.color } : { label: status, color: "#8c8c8c" };
};

export const CustomerEditDrawer = forwardRef(function CustomerEditDrawer({ customer, open, onClose, onSaved, defaultTab = "customer", defaultCreateOpen = false, imageMessages = [], imageMessagesLoading = false, onQueryCreated, nudgeTypeIfUnset = false, onFieldChanged, closeOnTypeSaved = false, onDirtyChange }, ref) {
  const { authState } = useContext(AuthContext);
  const { t, isDark } = useQv4Theme();

  // customer data tab state
  const [draft, setDraft] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [subForm, setSubForm] = useState(null);
  const [vehicleSubForm, setVehicleSubForm] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesSaving, setVehiclesSaving] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [activeSection, setActiveSection] = useState("addresses");
  const [saving, setSaving] = useState(false);

  // tab state
  const [activeTab, setActiveTab] = useState(["customer", "queries"].includes(defaultTab) ? defaultTab : "customer");
  // Consumed once per open — without this, any API call made while the
  // drawer is open (e.g. saving a field on the customer tab) refreshes the
  // `customer` prop, which re-runs the effect below and re-applies
  // defaultTab, yanking the user back to it mid-edit. Reset only when the
  // drawer actually (re)opens, not on every customer data refresh.
  const appliedDefaultTabRef = useRef(false);

  // queries tab state
  const [queriesList, setQueriesList] = useState([]);
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingQuery, setSavingQuery] = useState(false);
  const [sendQueryPk, setSendQueryPk] = useState(null);
  const [selectedQueryPk, setSelectedQueryPk] = useState(null);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [partsInfoOptions, setPartsInfoOptions] = useState([]);
  const [partsInfoLoading, setPartsInfoLoading] = useState(false);

  const autocompleteRef = useRef(null);
  const subFormRef = useRef(null);
  const vehicleSubFormRef = useRef(null);

  // Opt-in (ChatPanelHeader's avatar-opened flow uses this; other openers of
  // this same drawer — e.g. Operations/QuerysetV4's own usages — don't pass
  // it, so they're unaffected) — if the customer's type is still unset when
  // this drawer opens, scroll to and open the Type select below, plus a
  // one-off toast, instead of leaving staff to notice the empty field
  // themselves buried in this form.
  const [typeNudgeOpen, setTypeNudgeOpen] = useState(false);
  const [typeNudgePulsing, setTypeNudgePulsing] = useState(false);
  const typeFieldRef = useRef(null);

  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GMAPS_KEY,
    libraries: GMAPS_LIBS,
  });

  useEffect(() => {
    if (!open) { appliedDefaultTabRef.current = false; }
    if (!open || !customer) return;
    if (!appliedDefaultTabRef.current) {
      // Guard against unknown tab keys — an unmatched activeKey renders the
      // Tabs with nothing selected and hides the Save footer
      setActiveTab(["customer", "queries"].includes(defaultTab) ? defaultTab : "customer");
      appliedDefaultTabRef.current = true;
    }
    setDraft({
      customer_name: customer.customer_name || "",
      customer_real_name: customer.customer_real_name || "",
      customer_email: customer.customer_email || "",
      source: customer.source ?? null,
      status: customer.status ? true : false,
      type: customer.type ?? null,
    });
    setAddresses(
      parseAddresses(customer.customer_addresses_new ?? customer.addresses)
        .map((a, i) => a.id != null ? a : { ...a, id: `existing-${i}` })
    );
    setSubForm(null);
    setVehicleSubForm(null);
    setEditingVehicleId(null);
    setModelOptions([]);
    setActiveSection("vehicles");
    setCreateFormOpen(defaultCreateOpen ?? false);
    setCreateForm({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });
    loadVehicles(customer?.customer_id);
  }, [open, customer]); // eslint-disable-line

  useEffect(() => {
    if (!open || !customer || !nudgeTypeIfUnset || customer.type != null) {
      setTypeNudgeOpen(false);
      setTypeNudgePulsing(false);
      return;
    }
    message.info({
      // Fixed key (not per-customer) — see ChatPanelHeader's matching nudge
      // for why: duration:0 means it only clears on dismissal, so a fixed
      // key replaces rather than stacks across drawer opens.
      key: "customer-type-nudge-drawer",
      content: "Customer type not set — this customer doesn't have a type yet, please choose B2B or B2C below.",
      duration: 0,
    });
    // Give the drawer's own slide-in transition a moment to finish before
    // scrolling — doing it immediately can fight the transition and land in
    // the wrong spot.
    const openTimer = setTimeout(() => {
      typeFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTypeNudgeOpen(true);
      setTypeNudgePulsing(true);
    }, 350);
    const pulseTimer = setTimeout(() => setTypeNudgePulsing(false), 4000);
    return () => { clearTimeout(openTimer); clearTimeout(pulseTimer); };
  }, [open, customer, nudgeTypeIfUnset]); // eslint-disable-line

  useEffect(() => {
    if (sourceOptions.length > 0) return;
    fetchMiscOptions("query_source").then(setSourceOptions);
  }, []); // eslint-disable-line

  const [conditionOptions, setConditionOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [fuelTypeOptions, setFuelTypeOptions] = useState([]);
  const [sideOptions, setSideOptions] = useState([]);
  useEffect(() => {
    if (!open) return;
    if (conditionOptions.length === 0) fetchMiscOptions("part_condition_origin").then(setConditionOptions);
    if (regionOptions.length === 0) fetchRegionOptions().then(setRegionOptions);
    if (fuelTypeOptions.length === 0) fetchFuelTypeOptions().then(setFuelTypeOptions);
    if (sideOptions.length === 0) fetchMiscOptions("part_side").then(setSideOptions);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open || brandOptions.length > 0) return;
    setLoadingBrands(true);
    axios.post(
      API_DOMAIN + "/whatsapp/get_makes_v2",
      { name: "" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    ).then(res => {
      if (res.data.success) {
        setBrandOptions(res.data.data.map(item => ({ value: item.id, label: item.name })));
      }
    }).catch(() => { }).finally(() => setLoadingBrands(false));
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (open && activeTab === "queries" && customer?.customer_id) {
      loadQueries();
    }
  }, [open, activeTab, customer?.customer_id]); // eslint-disable-line

  useEffect(() => {
    if (!createFormOpen) return;
    if (agentOptions.length === 0) {
      const selfName = authState.user?.name || authState.user?.username || "";
      if (selfName) setAgentOptions([{ id: authState.id, fullName: selfName }]);
      setAgentsLoading(true);
      _fetchSRMUsers().then(list => { setAgentOptions(list); setAgentsLoading(false); });
    }
    if (partsInfoOptions.length === 0) {
      setPartsInfoLoading(true);
      axios.post(`${API_DOMAIN}/whatsapp/get_parts_info`, {}, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      }).then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setPartsInfoOptions(list.map(p =>
          typeof p === "string" ? { value: p, label: p } : { value: p.value ?? p.name, label: p.label ?? p.name ?? p.value }
        ));
      }).catch(() => {}).finally(() => setPartsInfoLoading(false));
    }
  }, [createFormOpen]); // eslint-disable-line

  async function handleAddNewPartInfo(value, idx) {
    try {
      await axios.post(
        `${API_DOMAIN}/whatsapp/add_new_part_info`,
        { value },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setPartsInfoOptions(prev => [...prev, { value, label: value }]);
    } catch { message.error("Failed to save new part name"); return; }
    setCreateForm(f => {
      const parts = [...f.parts];
      parts[idx] = { ...parts[idx], part_name: value };
      return { ...f, parts };
    });
  }

  async function loadVehicleModels(brandId) {
    if (!brandId) { setModelOptions([]); return; }
    setLoadingModels(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/whatsapp/get_models_v2",
        { make: brandId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (res.data.success) {
        setModelOptions(res.data.data.map(item => ({ value: item.id, label: item.model })));
      }
    } catch { setModelOptions([]); }
    finally { setLoadingModels(false); }
  }

  async function loadVehicles(customerId) {
    if (!customerId) { setVehicles([]); return; }
    setVehiclesLoading(true);
    try {
      const res = await axios.get(
        `${API_DOMAIN}/customer_vehicles/get_by_customer?customer_id=${customerId}`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setVehicles(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { setVehicles([]); }
    finally { setVehiclesLoading(false); }
  }

  async function loadQueries() {
    if (!customer?.customer_id) return;
    setQueriesLoading(true);
    try {
      const res = await axios.get(`${API_DOMAIN}/queryset_v2/get`, {
        params: { customer_id: customer.customer_id },
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      if (res.data.success) setQueriesList(res.data.data || []);
    } catch { setQueriesList([]); }
    finally { setQueriesLoading(false); }
  }

  async function handleCreateQuery(status = "draft") {
    if (!customer?.customer_id) return;
    const setSaving = status === "draft" ? setSavingDraft : setSavingQuery;
    setSaving(true);
    try {
      const headers = { accessToken: localStorage.getItem("accessToken") };

      // Upload all images to the server and collect permanent URLs
      // - device images: upload File object directly
      // - chat images: resolve WATI signed URL → fetch blob → upload
      // Each image's own uploadStatus ("uploading"/"done"/"error") is set as
      // its own upload settles — QueryImagePicker's thumbnail renders that
      // directly, so the spinner/check/x reflects real per-image progress
      // instead of one all-or-nothing state on the submit button. A single
      // failed image still blocks the whole submit (same as before), just
      // with visibility into which one to retry/remove.
      let allImages = [];
      if (createForm.images.length > 0) {
        setCreateForm(f => ({ ...f, images: f.images.map(img => ({ ...img, uploadStatus: "uploading" })) }));
        const results = await Promise.all(
          createForm.images.map(async img => {
            try {
              const fd = new FormData();
              if (img.source === "upload") {
                fd.append("file", img.file);
              } else {
                // Use the existing media download proxy to avoid CORS, then re-upload
                const blobRes = await axios.post(
                  `${API_DOMAIN}/wati_messages/download_wati_media_file`,
                  { file_name: img.data },
                  { headers, responseType: "blob" }
                );
                const blob = blobRes.data;
                const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
                fd.append("file", new File([blob], `chat_image_${Date.now()}.${ext}`, { type: blob.type }));
              }
              const res = await axios.post(`${API_DOMAIN}/whatsapp/image_upload`, fd, { headers });
              setCreateForm(f => ({ ...f, images: f.images.map(x => x.id === img.id ? { ...x, uploadStatus: "done" } : x) }));
              return { id: img.id, data: res.data, ok: true };
            } catch {
              setCreateForm(f => ({ ...f, images: f.images.map(x => x.id === img.id ? { ...x, uploadStatus: "error" } : x) }));
              return { id: img.id, ok: false };
            }
          })
        );
        if (results.some(r => !r.ok)) {
          message.error("Failed to upload one or more images. Please try again.");
          setSaving(false);
          return;
        }
        allImages = results.map(r => r.data);
      }

      const validParts = createForm.parts
        .filter(p => p.part_name?.trim())
        .map(p => ({
          part_name: p.part_name.trim(),
          quantity: p.quantity || 1,
          ...(p.side && { side: p.side }),
          ...(p.condition_preference && { condition_preference: p.condition_preference }),
          ...(p.part_number?.trim() && { part_number: p.part_number.trim() }),
        }));
      const payload = {
        customer_id: customer.customer_id,
        created_by: authState.id,
        status,
        ...(createForm.vehicle_id != null && { vehicle_id: createForm.vehicle_id }),
        ...(createForm.urgency != null && { urgency: createForm.urgency }),
        ...(createForm.assigned_to != null && { assigned_to: createForm.assigned_to }),
        ...(createForm.remarks?.trim() && { remarks: createForm.remarks.trim() }),
        ...(validParts.length > 0 && { parts: validParts }),
        ...(allImages.length > 0 && { images: allImages }),
      };
      const res = await axios.post(
        `${API_DOMAIN}/queryset_v2/create`,
        payload,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      message.success("Query created");
      setCreateFormOpen(false);
      setCreateForm({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });

      // Inject card directly into the kanban board without a refetch
      const selectedVehicle = vehicles.find(v => v.id === createForm.vehicle_id) ?? null;
      const assignedAgent = agentOptions.find(a => a.id === createForm.assigned_to);
      const newCard = {
        id: res.data.data?.task_id,
        query_id: res.data.data?.query_id,
        status: res.data.data?.status ?? "draft",
        urgency: createForm.urgency,
        created_at: new Date().toISOString(),
        assigned_user: assignedAgent ? { name: assignedAgent.fullName } : null,
        vehicle: selectedVehicle ? {
          brand_name: selectedVehicle.brand_name || selectedVehicle.brand_label || selectedVehicle.brand || "",
          model_name: selectedVehicle.model_name || selectedVehicle.model_label || selectedVehicle.model || "",
          year: selectedVehicle.year || null,
        } : null,
        customer: {
          customer_name: customer.customer_real_name || customer.customer_name || null,
          customer_mobile: customer.customer_mobile || null,
        },
        query_parts: res.data.data?.query_parts || [],
        images: res.data.data?.images ?? allImages,
        remarks: res.data.data?.remarks ?? createForm.remarks ?? "",
        has_after_sales: false,
      };
      // Pass openSend=true when "Create Query" so the parent opens the send modal
      onQueryCreated?.(newCard, status === "waiting_for_price");

      await loadQueries();
    } catch (e) {
      message.error(e.message || "Failed to create query");
    } finally {
      setSaving(false);
    }
  }

  const setField = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const openAddSubForm = () => {
    setActiveSection("addresses");
    setSubForm({
      name: customer?.customer_real_name?.trim() || customer?.customer_name?.trim() || "",
      number: customer?.customer_mobile || "",
      address: "",
      type: "Home",
      pref_time: null,
    });
    setTimeout(() => subFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const openAddVehicleForm = () => {
    setActiveSection("vehicles");
    setVehicleSubForm({
      brand_id: null, model_id: null, variant: "", year: null,
      vin: "", engine_code: "", engine_displacement: "", engine_layout: null,
      transmission_type: null, transmission_code: "", drivetrain: null,
      fuel_type: null, country_spec: null, trim_info: "", notes: "",
      is_primary: false, status: true,
    });
    setModelOptions([]);
    setTimeout(() => {
      const el = vehicleSubFormRef.current;
      if (!el) return;
      const container = el.closest(".ant-drawer-body");
      if (container) {
        const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 8;
        container.scrollTo({ top, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 350);
  };

  const editAddress = (addr) => setSubForm({ ...addr });
  const deleteAddress = (id) => setAddresses(prev => prev.filter(a => a.id !== id));

  const saveSubForm = () => {
    if (!subForm?.name?.trim() || !subForm?.number?.trim() || !subForm?.address?.trim()) {
      message.warning("Name, number and address are required");
      return;
    }
    const isExisting = addresses.some(a => a.id === subForm.id);
    if (isExisting) {
      setAddresses(prev => prev.map(a => a.id === subForm.id ? subForm : a));
    } else {
      setAddresses(prev => [...prev, { ...subForm, id: Date.now() }]);
    }
    setSubForm(null);
  };

  // Split out from handleSave so the outer unsaved-changes guard (see
  // ChatPanelHeader/ActiveChatDrawer's "Save & Close All") can drive a save
  // without also triggering this drawer's own onClose — it decides on its
  // own whether to cascade-close afterward.
  const buildSavePayload = () => {
    const payload = { customer_id: customer.customer_id };
    if (draft.customer_name !== (customer.customer_name || "")) payload.customer_name = draft.customer_name;
    if (draft.customer_real_name !== (customer.customer_real_name || "")) payload.customer_real_name = draft.customer_real_name;
    if (draft.customer_email !== (customer.customer_email || "")) payload.customer_email = draft.customer_email;
    if ((draft.status ? 1 : 0) !== (customer.status ? 1 : 0)) payload.status = draft.status ? 1 : 0;
    if (draft.type !== (customer.type ?? null)) payload.type = draft.type;

    const origAddresses = parseAddresses(customer.customer_addresses_new ?? customer.addresses)
      .map((a, i) => a.id != null ? a : { ...a, id: `existing-${i}` });
    if (JSON.stringify(addresses) !== JSON.stringify(origAddresses)) {
      payload.customer_addresses_new = addresses;
    }
    return payload;
  };

  // Returns "saved" | "nothing" | "error" — the outer guard treats "nothing"
  // the same as "saved" (safe to proceed closing) but keeps the drawer open
  // on "error" so the problem (e.g. the email validation below) is visible.
  const saveCustomer = async () => {
    if (!customer?.customer_id) return "error";
    if (emailError) { message.warning("Please fix the email address before saving"); return "error"; }
    const payload = buildSavePayload();
    if (Object.keys(payload).length === 1) {
      message.info("No changes to save");
      return "nothing";
    }
    setSaving(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        payload,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      message.success({ content: "Customer updated", duration: 2 });
      onSaved({ ...(res.data.data || {}), type: draft.type });
      return "saved";
    } catch (e) {
      message.error(e.message || "Failed to save customer");
      return "error";
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const result = await saveCustomer();
    if (result !== "error") onClose();
  };

  // Drives both the outer "unsaved changes" guard and the footer Save
  // button's enabled state — true only when there's something the Save
  // button itself would actually submit (source and type autosave
  // separately, so they don't count here).
  const topLevelDirty = !!customer?.customer_id && Object.keys(buildSavePayload()).length > 1;

  // Unsaved-changes signal for the outer chat drawer — both drawers used to
  // close together on an outside click (they key off the same chat-drawer
  // pinned state), silently dropping whatever was mid-entry here. Reported
  // up so ChatPanelHeader/ActiveChatDrawer can intercept that cascade and
  // ask first instead. Any open vehicle/address subform or new-query form
  // counts as dirty regardless of what's been typed into it — it can't be
  // saved automatically (unvalidated), so the safest default is to always
  // warn rather than risk silently losing typed-but-unsubmitted entries.
  const hasUnsavedChanges = open && !!customer?.customer_id &&
    (topLevelDirty || subForm !== null || vehicleSubForm !== null || createFormOpen);
  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges]); // eslint-disable-line
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line

  useImperativeHandle(ref, () => ({ saveCustomer }));

  const [sourceSaving, setSourceSaving] = useState(false);

  const handleSourceChange = async (val) => {
    const newSource = val ?? null;
    setField("source", newSource);
    if (!customer?.customer_id) return;
    setSourceSaving(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        { customer_id: customer.customer_id, source: newSource },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      message.success({ content: "Source updated", duration: 2 });
      onFieldChanged?.({ customer_id: customer.customer_id, source: newSource });
    } catch (e) {
      message.error(e.message || "Failed to update source");
    } finally {
      setSourceSaving(false);
    }
  };

  const [typeSaving, setTypeSaving] = useState(false);

  const handleTypeChange = async (val) => {
    const newType = val ?? null;
    setField("type", newType);
    setTypeNudgeOpen(false);
    if (!customer?.customer_id) return;
    setTypeSaving(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        { customer_id: customer.customer_id, type: newType },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      // Either nudge toast could still be sitting there unresolved — the
      // agent may have opened this drawer some other way (e.g. clicking the
      // avatar directly) while the header's own ambient nudge was still up.
      // A successful save resolves both, regardless of which route got them
      // here.
      message.destroy("customer-type-nudge-drawer");
      message.destroy("customer-type-nudge-header");
      message.success({ content: "Customer type updated", duration: 2 });
      onFieldChanged?.({ customer_id: customer.customer_id, type: newType });
      // Only auto-close when the drawer was opened specifically via the
      // header's "Set Now" button — that flow exists purely to set the
      // type, so once it's set there's nothing left to do here. A drawer
      // opened by clicking the avatar directly may have been opened for
      // some other reason, so it stays open even after the type is set.
      if (closeOnTypeSaved) onClose();
    } catch (e) {
      message.error(e.message || "Failed to save customer type");
    } finally {
      setTypeSaving(false);
    }
  };

  const [copyingWatiName, setCopyingWatiName] = useState(false);

  const copyWatiName = async () => {
    if (!customer?.customer_id || !draft.customer_name?.trim()) return;
    const nameToCopy = draft.customer_name;
    setField("customer_real_name", nameToCopy);
    setCopyingWatiName(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        { customer_id: customer.customer_id, customer_real_name: nameToCopy },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      message.success({ content: "Real name updated", duration: 2 });
      onSaved({ ...(res.data.data || {}), type: draft.type });
    } catch (e) {
      message.error(e.message || "Failed to update real name");
    } finally {
      setCopyingWatiName(false);
    }
  };

  const customerDataTab = (
    <>
      <div style={{ display: "grid", gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Customer Name</div>
          <Input
            value={draft.customer_name || ""}
            onChange={e => setField("customer_name", e.target.value)}
            placeholder="Display name"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
            Real Name
            {!!draft.customer_name?.trim() && (
              <Tooltip title="Copy WhatsApp name into this field">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  loading={copyingWatiName}
                  onClick={copyWatiName}
                  style={{ padding: "0 4px", height: 18, lineHeight: "18px" }}
                />
              </Tooltip>
            )}
          </div>
          <Input
            value={draft.customer_real_name || ""}
            onChange={e => setField("customer_real_name", e.target.value)}
            placeholder="Legal / real name"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Email</div>
          <Input
            value={draft.customer_email || ""}
            onChange={e => {
              const v = e.target.value;
              setField("customer_email", v);
              if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) setEmailError("Invalid email address");
              else setEmailError("");
            }}
            placeholder="Email address"
            status={emailError ? "error" : ""}
          />
          {emailError && <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 2 }}>{emailError}</div>}
        </div>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Phone</div>
          <div style={{ fontSize: 13, color: t.text, padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
            <span>{customer?.customer_mobile || "—"}</span>
            {(customer?.country || customer?.customer_country) && (
              <span style={{ fontSize: 12, color: t.textSub }}>
                {customer?.country || customer?.customer_country}
              </span>
            )}
            {!!customer?.customer_mobile && (
              <Tooltip title="Copy phone number">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(customer.customer_mobile);
                    message.success({ content: "Copied", duration: 1.5 });
                  }}
                  style={{ padding: "0 4px", height: 18, lineHeight: "18px" }}
                />
              </Tooltip>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Source</div>
            <Select
              style={{ width: "100%" }}
              value={draft.source ?? null}
              onChange={handleSourceChange}
              loading={sourceSaving}
              disabled={sourceSaving}
              allowClear
              placeholder="Select source"
              options={sourceOptions.map(o => ({ value: o.value, label: o.label }))}
            />
          </div>
          <div ref={typeFieldRef} style={{ borderRadius: 6, animation: typeNudgePulsing ? "pulse 1s ease-in-out infinite" : "none" }}>
            <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Type</div>
            <Select
              style={{ width: 90 }}
              value={draft.type ?? null}
              onChange={handleTypeChange}
              open={typeNudgeOpen || undefined}
              onDropdownVisibleChange={setTypeNudgeOpen}
              loading={typeSaving}
              disabled={typeSaving}
              allowClear
              placeholder="Type?"
              options={[
                { value: 0, label: <span style={{ color: "#1677ff", fontWeight: 600 }}>B2B</span> },
                { value: 1, label: <span style={{ color: "#722ed1", fontWeight: 600 }}>B2C</span> },
              ]}
            />
          </div>
        </div>
      </div>

      <Tabs
        style={{ marginTop: 20 }}
        activeKey={activeSection}
        onChange={setActiveSection}
        // Sticky within the drawer's own scroll container (the `overflowY:
        // auto` div this whole tab lives in) — B2B customers can have long
        // enough vehicle/address lists that scrolling through them would
        // otherwise carry the tab switcher and Add button off-screen too.
        tabBarStyle={{ position: "sticky", top: 0, zIndex: 2, background: t.cardBg, marginBottom: 0, paddingBottom: 12 }}
        tabBarExtraContent={
          activeSection === "vehicles"
            ? <Button size="small" icon={<PlusOutlined />} onClick={openAddVehicleForm}>Add</Button>
            : activeSection === "addresses"
              ? <Button size="small" icon={<PlusOutlined />} onClick={openAddSubForm}>Add</Button>
              : null
        }
        items={[
          {
            key: "vehicles",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 0 }}>
                <CarOutlined />
                Vehicles
                {vehicles.length > 0 && (
                  <Badge count={vehicles.length} size="small" color={activeSection === "vehicles" ? "#1677ff" : "#8c8c8c"} />
                )}
              </span>
            ),
            children: (
              <>
                {vehicleSubForm !== null && (
                  <div ref={vehicleSubFormRef}
                    style={{ border: `1px solid ${editingVehicleId ? "#d46b08" : "#1677ff"}`, borderRadius: 6, padding: 12, background: editingVehicleId ? (isDark ? "rgba(250,173,20,0.12)" : "#fff7e6") : (isDark ? "rgba(24,144,255,0.12)" : "#f0f5ff"), marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: editingVehicleId ? "#d46b08" : "#1677ff", marginBottom: 8 }}>
                      {editingVehicleId ? "Edit Vehicle" : "Add New Vehicle"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Brand</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingBrands}
                          placeholder="Select brand"
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={brandOptions}
                          value={vehicleSubForm.brand_id ?? null}
                          onChange={val => {
                            setVehicleSubForm(f => ({ ...f, brand_id: val ?? null, model_id: null }));
                            loadVehicleModels(val);
                          }}
                          allowClear
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Model</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingModels}
                          placeholder={vehicleSubForm.brand_id ? "Select model" : "Select brand first"}
                          disabled={!vehicleSubForm.brand_id}
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={modelOptions}
                          value={vehicleSubForm.model_id ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, model_id: val ?? null }))}
                          allowClear
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Variant</div>
                        <Input
                          placeholder="e.g. Sport, LX, SE"
                          value={vehicleSubForm.variant}
                          onChange={e => setVehicleSubForm(f => ({ ...f, variant: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Year</div>
                        <DatePicker
                          style={{ width: "100%" }}
                          picker="year"
                          placeholder="Select year"
                          value={vehicleSubForm.year ? dayjs(vehicleSubForm.year, "YYYY") : null}
                          onChange={(_, str) => setVehicleSubForm(f => ({ ...f, year: str || null }))}
                          disabledDate={d => d && (d.year() < 2000 || d.year() > dayjs().year())}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>VIN / Chassis No.</div>
                        <Input
                          placeholder="e.g. 1HGCM82633A004352"
                          value={vehicleSubForm.vin}
                          onChange={e => setVehicleSubForm(f => ({ ...f, vin: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Engine Code</div>
                        <Input
                          placeholder="e.g. 2AR-FE"
                          value={vehicleSubForm.engine_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, engine_code: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Engine Displacement (L)</div>
                        <Input
                          placeholder="e.g. 2.0"
                          value={vehicleSubForm.engine_displacement}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === "" || /^\d{0,2}(\.\d{0,1})?$/.test(val)) {
                              setVehicleSubForm(f => ({ ...f, engine_displacement: val }));
                            }
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Engine Layout</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select layout"
                          allowClear
                          value={vehicleSubForm.engine_layout ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, engine_layout: val ?? null }))}
                          options={[
                            { value: "inline-3", label: "Inline-3" },
                            { value: "inline-4", label: "Inline-4" },
                            { value: "inline-6", label: "Inline-6" },
                            { value: "v6", label: "V6" },
                            { value: "v8", label: "V8" },
                            { value: "v10", label: "V10" },
                            { value: "v12", label: "V12" },
                            { value: "flat-4", label: "Flat-4" },
                            { value: "flat-6", label: "Flat-6" },
                            { value: "w12", label: "W12" },
                            { value: "w16", label: "W16" },
                            { value: "rotary", label: "Rotary" },
                            { value: "electric", label: "Electric Motor" },
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Transmission Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select type"
                          allowClear
                          value={vehicleSubForm.transmission_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, transmission_type: val ?? null }))}
                          options={[
                            { value: "automatic", label: "Automatic" },
                            { value: "manual", label: "Manual" },
                            { value: "cvt", label: "CVT" },
                            { value: "dct", label: "DCT (Dual Clutch)" },
                            { value: "amt", label: "AMT" },
                            { value: "semi-automatic", label: "Semi-Automatic" },
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Transmission Code</div>
                        <Input
                          placeholder="e.g. A750E"
                          value={vehicleSubForm.transmission_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, transmission_code: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Drivetrain</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select drivetrain"
                          allowClear
                          value={vehicleSubForm.drivetrain ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, drivetrain: val ?? null }))}
                          options={[
                            { value: "fwd", label: "FWD" },
                            { value: "rwd", label: "RWD" },
                            { value: "awd", label: "AWD" },
                            { value: "4wd", label: "4WD" },
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Fuel Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select fuel type"
                          allowClear
                          value={vehicleSubForm.fuel_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, fuel_type: val ?? null }))}
                          options={fuelTypeOptions}
                          showSearch
                          optionFilterProp="label"
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Country Spec</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select spec"
                          allowClear
                          value={vehicleSubForm.country_spec ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, country_spec: val ?? null }))}
                          options={regionOptions}
                          showSearch
                          optionFilterProp="label"
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Trim Info</div>
                        <Input
                          placeholder="e.g. Sport Edition, Night Package"
                          value={vehicleSubForm.trim_info}
                          onChange={e => setVehicleSubForm(f => ({ ...f, trim_info: e.target.value }))}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Additional Notes</div>
                        <Input.TextArea
                          rows={3}
                          placeholder="Any extra details about the vehicle..."
                          value={vehicleSubForm.notes}
                          onChange={e => setVehicleSubForm(f => ({ ...f, notes: e.target.value }))}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Checkbox
                          checked={vehicleSubForm.is_primary}
                          onChange={e => setVehicleSubForm(f => ({ ...f, is_primary: e.target.checked }))}
                        >
                          <span style={{ fontSize: 12 }}>Primary vehicle</span>
                        </Checkbox>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: t.textSub }}>Active</span>
                          <Switch
                            size="small"
                            checked={vehicleSubForm.status}
                            onChange={val => setVehicleSubForm(f => ({ ...f, status: val }))}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                      <Button size="small" onClick={() => { setVehicleSubForm(null); setEditingVehicleId(null); setModelOptions([]); }}>Cancel</Button>
                      <Button size="small" type="primary" loading={vehiclesSaving}
                        style={editingVehicleId ? { background: "#d46b08", borderColor: "#d46b08" } : {}}
                        onClick={async () => {
                          if (!vehicleSubForm.brand_id) { message.warning("Please select a brand"); return; }
                          if (!vehicleSubForm.model_id) { message.warning("Please select a model"); return; }
                          setVehiclesSaving(true);
                          const isEdit = !!editingVehicleId;
                          try {
                            const vf = vehicleSubForm;
                            const payload = isEdit
                              ? { id: editingVehicleId }
                              : { customer_id: customer.customer_id, brand: vf.brand_id, model: vf.model_id, created_by: authState.id };
                            if (!isEdit || true) {
                              payload.brand = vf.brand_id;
                              payload.model = vf.model_id;
                              payload.is_primary_vehicle = vf.is_primary ? 1 : 0;
                              payload.status = vf.status ? 1 : 0;
                              payload.variant = vf.variant || null;
                              payload.year = vf.year || null;
                              payload.vin = vf.vin || null;
                              payload.engine_code = vf.engine_code || null;
                              payload.engine_displacement = vf.engine_displacement ? parseFloat(vf.engine_displacement) : null;
                              payload.engine_layout = vf.engine_layout || null;
                              payload.transmission_type = vf.transmission_type || null;
                              payload.transmission_code = vf.transmission_code || null;
                              payload.drivetrain = vf.drivetrain || null;
                              payload.fuel_type = vf.fuel_type || null;
                              payload.country_spec = vf.country_spec || null;
                              payload.trim_info = vf.trim_info || null;
                              payload.notes = vf.notes || null;
                            }
                            const endpoint = isEdit ? "/customer_vehicles/update" : "/customer_vehicles/create";
                            const res = await axios.post(
                              API_DOMAIN + endpoint,
                              payload,
                              { headers: { accessToken: localStorage.getItem("accessToken") } }
                            );
                            if (!res.data.success) throw new Error(res.data.message);
                            message.success(isEdit ? "Vehicle updated" : "Vehicle added");
                            setVehicleSubForm(null);
                            setEditingVehicleId(null);
                            setModelOptions([]);
                            await loadVehicles(customer.customer_id);
                          } catch (e) {
                            message.error(e.message || (isEdit ? "Failed to update vehicle" : "Failed to add vehicle"));
                          } finally {
                            setVehiclesSaving(false);
                          }
                        }}>
                        {editingVehicleId ? "Save Changes" : "Add Vehicle"}
                      </Button>
                    </div>
                  </div>
                )}
                {vehiclesLoading
                  ? <div style={{ textAlign: "center", padding: "16px 0" }}><Spin size="small" /></div>
                  : vehicles.length === 0
                    ? <Empty description="No vehicles yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    : vehicles.map(v => {
                      const brandLabel = v.brand_name || v.brand_label || (brandOptions.find(b => b.value === v.brand)?.label) || v.brand || "—";
                      const modelLabel = v.model_name || v.model_label || v.model || "—";
                      const isPrimary = v.is_primary_vehicle === 1 || v.is_primary === true;
                      const isActive = v.status !== 0 && v.status !== false;
                      return (
                        <div key={v.id}
                          style={{
                            border: `1px solid ${t.border}`, borderRadius: 6, padding: "8px 10px",
                            marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "flex-start"
                          }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {brandLabel} {modelLabel} {v.year ? `(${v.year})` : ""}
                              {isPrimary && <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>Primary</Tag>}
                              {!isActive && <Tag color="default" style={{ marginLeft: 4, fontSize: 10 }}>Inactive</Tag>}
                            </div>
                            {v.variant && <div style={{ fontSize: 11, color: t.textSub }}>Variant: {v.variant}</div>}
                            {v.vin && <div style={{ fontSize: 11, color: t.textSub }}>VIN: {v.vin}</div>}
                            {v.engine_displacement && <div style={{ fontSize: 11, color: t.textSub }}>{v.engine_displacement}L{v.engine_layout ? ` · ${engineLayoutLabel(v.engine_layout)}` : ""}{v.fuel_type ? ` · ${v.fuel_type}` : ""}</div>}
                          </div>
                          <div style={{ display: "flex", gap: 2 }}>
                            <Tooltip title="Edit vehicle">
                              <Button type="text" size="small" icon={<EditOutlined />}
                                style={{ color: "#1677ff" }}
                                onClick={() => {
                                  setActiveSection("vehicles");
                                  setEditingVehicleId(v.id);
                                  setVehicleSubForm({
                                    brand_id: v.brand ?? null,
                                    model_id: v.model ?? null,
                                    variant: v.variant || "",
                                    year: v.year ? String(v.year).slice(0, 4) : null,
                                    vin: v.vin || "",
                                    engine_code: v.engine_code || "",
                                    engine_displacement: v.engine_displacement != null ? String(v.engine_displacement) : "",
                                    engine_layout: v.engine_layout || null,
                                    transmission_type: v.transmission_type || null,
                                    transmission_code: v.transmission_code || "",
                                    drivetrain: v.drivetrain || null,
                                    fuel_type: v.fuel_type || null,
                                    country_spec: v.country_spec || null,
                                    trim_info: v.trim_info || "",
                                    notes: v.notes || "",
                                    is_primary: v.is_primary_vehicle === 1,
                                    status: v.status !== 0,
                                  });
                                  if (v.brand) loadVehicleModels(v.brand);
                                  setTimeout(() => vehicleSubFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                                }}
                              />
                            </Tooltip>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />}
                              onClick={async () => {
                                try {
                                  const res = await axios.post(
                                    API_DOMAIN + "/customer_vehicles/delete",
                                    { id: v.id },
                                    { headers: { accessToken: localStorage.getItem("accessToken") } }
                                  );
                                  if (!res.data.success) throw new Error(res.data.message);
                                  setVehicles(prev => prev.filter(x => x.id !== v.id));
                                } catch (e) {
                                  message.error(e.message || "Failed to delete vehicle");
                                }
                              }} />
                          </div>
                        </div>
                      );
                    })
                }
              </>
            ),
          },

          {
            key: "addresses",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EnvironmentOutlined />
                Addresses
                {addresses.length > 0 && (
                  <Badge count={addresses.length} size="small" color={activeSection === "addresses" ? "#1677ff" : "#8c8c8c"} />
                )}
              </span>
            ),
            children: (
              <>
                {subForm !== null && (
                  <div ref={subFormRef}
                    style={{
                      border: "1px solid #1677ff", borderRadius: 6, padding: 12, background: isDark ? "rgba(24,144,255,0.12)" : "#f0f5ff",
                      display: "grid", gap: 8, marginBottom: 12
                    }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Input placeholder="Name *" value={subForm.name || ""}
                        onChange={e => setSubForm(f => ({ ...f, name: e.target.value }))} />
                      <Input placeholder="Number *" value={subForm.number || ""}
                        onChange={e => setSubForm(f => ({ ...f, number: e.target.value.replace(/\D/g, "") }))} />
                    </div>
                    {isMapsLoaded ? (
                      <Autocomplete
                        onLoad={ac => { autocompleteRef.current = ac; }}
                        onPlaceChanged={() => {
                          const place = autocompleteRef.current?.getPlace();
                          if (place?.formatted_address)
                            setSubForm(f => ({ ...f, address: place.formatted_address }));
                        }}
                      >
                        <Input placeholder="Address *" value={subForm.address || ""}
                          onChange={e => setSubForm(f => ({ ...f, address: e.target.value }))} />
                      </Autocomplete>
                    ) : (
                      <Input placeholder="Address *" value={subForm.address || ""}
                        onChange={e => setSubForm(f => ({ ...f, address: e.target.value }))} />
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Radio.Group value={subForm.type || "Home"}
                        onChange={e => setSubForm(f => ({ ...f, type: e.target.value }))}
                        options={[{ label: "Home", value: "Home" }, { label: "Office", value: "Office" }]}
                        optionType="button" buttonStyle="solid" size="small" />
                      <TimePicker.RangePicker format="hh:mm A" use12Hours allowClear size="small"
                        value={subForm.pref_time?.length === 2
                          ? [dayjs(subForm.pref_time[0]), dayjs(subForm.pref_time[1])]
                          : null}
                        onChange={val => setSubForm(f => ({
                          ...f, pref_time: val ? [val[0].toISOString(), val[1].toISOString()] : null,
                        }))} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      <Button size="small" onClick={() => setSubForm(null)}>Cancel</Button>
                      <Button size="small" type="primary" onClick={saveSubForm}>Save Address</Button>
                    </div>
                  </div>
                )}

                <div style={{ display: "grid", gap: 8 }}>
                  {addresses.length === 0 && (
                    <Empty description="No addresses" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                  {addresses.map(addr => (
                    <div key={addr.id}
                      style={{ border: `1px solid ${t.border}`, borderRadius: 6, padding: "8px 12px", background: t.headerBg }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <span style={{ fontSize: 11, background: t.hover, color: t.textSub, padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>
                          {addr.type}
                        </span>
                        <div style={{ display: "flex", gap: 4 }}>
                          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => editAddress(addr)} />
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteAddress(addr.id)} />
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, marginTop: 4 }}>
                        {addr.name}
                        <span style={{ fontWeight: 400, color: t.textSub, fontSize: 12, marginLeft: 8 }}>{addr.number}</span>
                      </div>
                      <div style={{ fontSize: 12, color: t.textSub, marginTop: 2 }}>{addr.address}</div>
                      {addr.pref_time && Array.isArray(addr.pref_time) && addr.pref_time.length === 2 && (
                        <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>
                          {dayjs(addr.pref_time[0]).format("hh:mm A")} – {dayjs(addr.pref_time[1]).format("hh:mm A")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ),
          },
        ].filter(Boolean)}
      />
    </>
  );

  const vehicleOptions = vehicles.map(v => {
    const brand = v.brand_name || v.brand_label || v.brand || "";
    const model = v.model_name || v.model_label || v.model || "";
    const year = v.year ? ` (${v.year})` : "";
    const spec = v.country_spec ? ` (${v.country_spec})` : "";
    return { value: v.id, label: `${brand} ${model}${year}${spec}`.trim() };
  });

  const queriesTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Create form toggle */}
      {!createFormOpen ? (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setCreateFormOpen(true)}
          block
        >
          New Query
        </Button>
      ) : (
        <div style={{
          border: "1px solid #1677ff", borderRadius: 8,
          padding: 14, background: isDark ? "rgba(24,144,255,0.12)" : "#f0f5ff",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#1677ff" }}>New Query</div>

          {/* Vehicle */}
          <div>
            <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Vehicle</div>
            {vehicles.length === 0 ? (
              <div style={{ fontSize: 12, color: "#d48806", display: "flex", alignItems: "center", gap: 6 }}>
                <CarOutlined />
                No vehicles on record —{" "}
                <span
                  style={{ color: "#1677ff", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => { setActiveTab("customer"); openAddVehicleForm(); }}
                >
                  add one in Customer Data
                </span>
              </div>
            ) : (
              <Select
                style={{ width: "100%" }}
                placeholder="Select vehicle"
                allowClear
                value={createForm.vehicle_id ?? null}
                onChange={val => setCreateForm(f => ({ ...f, vehicle_id: val ?? null }))}
                options={vehicleOptions}
              />
            )}
          </div>

          {/* Urgency + Assigned To */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Urgency</div>
              <Select
                style={{ width: "100%" }}
                value={createForm.urgency}
                onChange={val => setCreateForm(f => ({ ...f, urgency: val }))}
                options={Object.entries(URGENCY_CFG).map(([k, v]) => ({
                  value: Number(k),
                  label: v.label,
                }))}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Assign To</div>
              {authState.user?.admin ? (
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select agent"
                  loading={agentsLoading}
                  value={createForm.assigned_to ?? null}
                  onChange={val => setCreateForm(f => ({ ...f, assigned_to: val ?? authState.id }))}
                  options={agentOptions.map(a => ({ value: a.id, label: a.fullName }))}
                  optionRender={option => {
                    const name = String(option.label ?? option.value ?? "");
                    return (
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Avatar size={14} style={{ backgroundColor: getAgentColor(name), fontSize: 8, flexShrink: 0 }}>
                          {agentInitials(name)}
                        </Avatar>
                        {name}
                      </span>
                    );
                  }}
                />
              ) : (() => {
                const me = agentOptions.find(a => a.id === authState.id);
                const name = me?.fullName || authState.user?.name || authState.user?.username || "You";
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", border: `1px solid ${t.border}`, borderRadius: 6, background: t.headerBg }}>
                    <Avatar size={16} style={{ backgroundColor: getAgentColor(name), fontSize: 9, flexShrink: 0 }}>
                      {agentInitials(name)}
                    </Avatar>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{name}</span>
                    <span style={{ fontSize: 10, color: t.textSub, marginLeft: "auto" }}>You</span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Remarks */}
          <div>
            <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Remarks</div>
            <Input.TextArea
              rows={2}
              placeholder="Notes about this query..."
              value={createForm.remarks}
              onChange={e => setCreateForm(f => ({ ...f, remarks: e.target.value }))}
            />
          </div>

          {/* Parts */}
          <PartsEditor
            parts={createForm.parts}
            setParts={updater => setCreateForm(f => ({ ...f, parts: typeof updater === "function" ? updater(f.parts) : updater }))}
            sideOptions={sideOptions}
            conditionOptions={conditionOptions}
            partsInfoOptions={partsInfoOptions}
            partsInfoLoading={partsInfoLoading}
            onAddNewPartInfo={handleAddNewPartInfo}
          />

          {/* Images */}
          <QueryImagePicker
            images={createForm.images}
            setImages={updater => setCreateForm(f => ({ ...f, images: typeof updater === "function" ? updater(f.images) : updater }))}
            imageMessages={imageMessages}
            imageMessagesLoading={imageMessagesLoading}
          />

          {/* Action buttons */}
          {(() => {
            const hasVehicle = createForm.vehicle_id != null;
            const hasparts = createForm.parts.some(p => p.part_name?.trim());
            const hasImages = createForm.images.length > 0;
            const canCreate = hasVehicle && hasparts && hasImages;
            return (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 2 }}>
                <Button size="small" disabled={savingDraft || savingQuery} onClick={() => {
                  setCreateFormOpen(false);
                  setCreateForm({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });
                }}>
                  Cancel
                </Button>
                <Button size="small" loading={savingDraft} disabled={savingQuery} onClick={() => handleCreateQuery("draft")}>
                  Save Draft
                </Button>
                <Tooltip title={!canCreate ? "Add vehicle, at least one part, and at least one image to create a query" : ""}>
                  <Button
                    size="small"
                    type="primary"
                    disabled={!canCreate || savingDraft}
                    loading={savingQuery}
                    onClick={() => handleCreateQuery("waiting_for_price")}
                  >
                    Create Query
                  </Button>
                </Tooltip>
              </div>
            );
          })()}

          <SendToSuppliersModal
            queryPk={sendQueryPk}
            open={!!sendQueryPk}
            onClose={() => setSendQueryPk(null)}
            onSent={() => setSendQueryPk(null)}
          />
        </div>
      )}

      {/* Query list */}
      {queriesLoading ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}><Spin /></div>
      ) : queriesList.length === 0 ? (
        <Empty description="No queries yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 24 }} />
      ) : (
        queriesList.map(q => {
          const st = getStatusDisplay(q.status);
          const v = q.vehicle;
          const vehicleLabel = v
            ? [v.brand_name, v.model_name, v.year].filter(Boolean).join(" ")
            : null;
          const urgCfg = q.urgency ? URGENCY_CFG[q.urgency] : null;
          const assignee = q.assigned_user;
          const assigneeName = assignee
            ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
            : null;

          return (
            <div
              key={q.query_pk}
              onClick={() => setSelectedQueryPk(q.query_pk)}
              style={{
                border: `1px solid ${t.border}`,
                borderLeft: `4px solid ${st.color}`,
                borderRadius: 6,
                padding: "10px 12px",
                cursor: "pointer",
                background: t.cardBg,
                transition: "box-shadow 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.10)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: "#1677ff", fontSize: 13 }}>#{q.query_id}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {urgCfg && (
                    <span style={{
                      padding: "1px 6px", borderRadius: 20, fontSize: 10, fontWeight: 600,
                      background: urgCfg.pill, color: urgCfg.color, border: `1px solid ${urgCfg.color}`,
                    }}>
                      {urgCfg.label.split(" · ")[1]}
                    </span>
                  )}
                  <Tag style={{ margin: 0, fontSize: 10, padding: "0 5px", color: st.color, borderColor: st.color, background: `${st.color}18` }}>
                    {st.label}
                  </Tag>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {vehicleLabel && (
                  <span style={{ fontSize: 12, color: t.text, display: "flex", alignItems: "center", gap: 4 }}>
                    <CarOutlined style={{ fontSize: 11, color: t.textSub }} />
                    {vehicleLabel}
                  </span>
                )}
                {assigneeName && (
                  <span style={{ fontSize: 11, color: t.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                    <Avatar size={14} style={{ backgroundColor: getAgentColor(assigneeName), fontSize: 8 }}>
                      {agentInitials(assigneeName)}
                    </Avatar>
                    {assigneeName}
                  </span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                {q.parts_count > 0 && (
                  <span style={{ fontSize: 11, color: t.textSub }}>
                    {q.parts_count} {q.parts_count === 1 ? "part" : "parts"}
                  </span>
                )}
                {q.created_at && (
                  <span style={{ fontSize: 11, color: t.textSub, display: "flex", alignItems: "center", gap: 3 }}>
                    <CalendarOutlined style={{ fontSize: 10 }} />
                    {dayjs(q.created_at).format("DD MMM YYYY")}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <>
      <Drawer
        placement="left"
        width={440}
        open={open}
        onClose={onClose}
        destroyOnHidden={false}
        mask={false}
        maskClosable={false}
        push={false}
        closable={false}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700 }}>Customer</div>
              <div style={{ fontSize: 12, color: t.textSub, fontWeight: 400 }}>
                {customer?.customer_name || customer?.customer_mobile || ""}
              </div>
            </div>
            <Tooltip title="Close" getPopupContainer={n => n.parentElement ?? document.body}>
              <Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={onClose} style={{ color: t.textSub }} />
            </Tooltip>
          </div>
        }
        footer={
          activeTab === "customer" ? (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button type="primary" loading={saving} disabled={!topLevelDirty} onClick={handleSave}>Save</Button>
            </div>
          ) : null
        }
        styles={{ body: { padding: 0 } }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ height: "100%" }}
          tabBarStyle={{ margin: 0, paddingLeft: 16, paddingRight: 16, borderBottom: `1px solid ${t.border}` }}
          items={[
            {
              key: "customer",
              label: "Customer Data",
              children: (
                // No overflowY/height:100% here on purpose — this div's
                // height never resolves against the tabpane's auto-height
                // ancestor, so as a "scroll container" it's inert (never
                // actually overflows). But `position: sticky` still binds
                // to the *nearest* such container regardless of whether it
                // scrolls in practice, which was hijacking the Vehicles/
                // Addresses tab bar's stickiness away from the real
                // scroller — `.ant-drawer-body` (antd's own flex:1,
                // min-height:0, overflow:auto). Letting the body be the
                // only overflow:auto ancestor is what makes that sticky bar
                // actually stick.
                <div style={{ padding: "16px 24px" }}>
                  {customerDataTab}
                </div>
              ),
            },
            {
              key: "queries",
              label: (
                <span>
                  Queries
                  {queriesList.length > 0 && (
                    <Badge count={queriesList.length} size="small" color="#8c8c8c" style={{ marginLeft: 6 }} />
                  )}
                </span>
              ),
              children: (
                <div style={{ padding: "16px 24px", overflowY: "auto", height: "100%" }}>
                  {queriesTab}
                </div>
              ),
            },
          ]}
        />
      </Drawer>

      <WorkflowTaskDrawer
        queryPk={selectedQueryPk}
        onClose={() => setSelectedQueryPk(null)}
      />
    </>
  );
});
